/**
 * Utility functions for Copy & Paste / Import feature of Job Opening Form
 * Version: CRM_JOB_REQUIREMENT_V1
 */

export const FORMAT_HEADER = '--- CRM JOB REQUIREMENT V1 ---';
export const FORMAT_FOOTER = '-----------------------------------';

/**
 * Sanitize string input to prevent XSS / malicious HTML injection
 */
export const sanitizeText = (str) => {
  if (typeof str !== 'string') return '';
  return str.replace(/<[^>]*>?/gm, '').trim();
};

export const generateCopyText = (params = {}) => {
  const {
    formData = {},
    salaryMonthly,
    jobTimingStart,
    jobTimingEnd,
  } = params || {};
  const company = formData.companyName || '';
  const branch = formData.branchName || '';
  const title = formData.jobTitle || '';
  const location = formData.jobLocation || '';
  const area = formData.Area || '';
  const openings = formData.numberOfRequirements != null ? String(formData.numberOfRequirements) : '';
  
  let timing = '';
  if (jobTimingStart && jobTimingEnd) {
    timing = `${jobTimingStart} - ${jobTimingEnd}`;
  } else if (jobTimingStart || jobTimingEnd) {
    timing = jobTimingStart || jobTimingEnd;
  } else if (formData.jobTiming) {
    timing = formData.jobTiming;
  }

  const education = formData.education || '';
  const gender = formData.gender || '';
  const salary = salaryMonthly != null ? String(salaryMonthly) : (formData.salary != null ? String(formData.salary) : '');
  const experience = formData.experience != null ? String(formData.experience) : '';
  const weekOff = formData.weekOff || '';
  const skills = formData.requiredSkills || '';
  const responsibilities = formData.keyResponsibility || '';
  const benefits = formData.benefits || '';
  const response = formData.response || '';

  const lines = [
    FORMAT_HEADER,
    `Company: ${company}`,
    `Branch: ${branch}`,
    `Job Title: ${title}`,
    `Job Location: ${location}`,
    `Area: ${area}`,
    `Openings: ${openings}`,
    `Timing: ${timing}`,
    `Education: ${education}`,
    `Gender: ${gender}`,
    `Monthly Salary (₹): ${salary}`,
    `Experience (Years): ${experience}`,
    `Week Off: ${weekOff}`,
    `Required Skills: ${skills}`,
    `Key Responsibilities: ${responsibilities}`,
    `Benefits: ${benefits}`,
    `Response: ${response}`,
    FORMAT_FOOTER,
  ];

  return lines.join('\n');
};

/**
 * Extract time string into start and end parts if possible
 */
const parseJobTimingString = (timingStr) => {
  if (!timingStr) return { start: '', end: '' };
  let start = '';
  let end = '';

  if (timingStr.includes('-')) {
    const parts = timingStr.split('-');
    start = parts[0].trim();
    end = parts[1].trim();
  } else if (timingStr.toLowerCase().includes(' to ')) {
    const parts = timingStr.toLowerCase().split(' to ');
    start = parts[0].trim();
    end = parts[1].trim();
  } else {
    start = timingStr.trim();
  }

  return { start, end };
};

/**
 * Helper to parse clean numeric value from strings like "25,000", "25000/month", "2.5 years"
 */
const parseNumericValue = (valStr) => {
  if (valStr == null) return null;
  const str = String(valStr).replace(/,/g, '').trim();
  if (!str) return null;
  
  // Extract first floating point / integer number
  const match = str.match(/[-+]?[0-9]*\.?[0-9]+/);
  if (match) {
    const num = parseFloat(match[0]);
    return isNaN(num) ? null : num;
  }
  return null;
};

/**
 * Parse imported text from client response
 */
export const parseImportText = (rawText, companyOptions = [], currentFormState = {}) => {
  const sanitized = sanitizeText(rawText);
  const lines = sanitized.split(/\r?\n/);
  
  const rawFields = {};
  const warnings = [];

  // Key map mapping various label variations to internal field keys
  const labelMap = [
    { key: 'companyName', labels: ['company', 'company name', 'companyname'] },
    { key: 'branchName', labels: ['branch', 'branch name', 'branchname'] },
    { key: 'jobTitle', labels: ['job title', 'title', 'position', 'role'] },
    { key: 'jobLocation', labels: ['job location', 'location', 'city'] },
    { key: 'Area', labels: ['area', 'locality'] },
    { key: 'numberOfRequirements', labels: ['openings', 'no. of openings', 'no of openings', 'number of requirements', 'requirements', 'vacancies', 'opening count'] },
    { key: 'jobTiming', labels: ['timing', 'job timing', 'working hours', 'work timing', 'time', 'start time / end time'] },
    { key: 'education', labels: ['education', 'qualification', 'educational qualification', 'degree'] },
    { key: 'gender', labels: ['gender', 'gender preference'] },
    { key: 'salaryMonthly', labels: ['monthly salary (₹)', 'monthly salary', 'salary (monthly ₹)', 'salary (monthly)', 'salary', 'pay', 'ctc'] },
    { key: 'experience', labels: ['experience (years)', 'experience (year)', 'experience', 'exp', 'exp (years)', 'required experience'] },
    { key: 'weekOff', labels: ['week off', 'weekly off', 'off day', 'off days', 'weekly off day'] },
    { key: 'requiredSkills', labels: ['required skills', 'skills', 'skill set', 'key skills', 'technical skills'] },
    { key: 'keyResponsibility', labels: ['key responsibilities', 'key responsibility', 'responsibilities', 'roles & responsibilities', 'job description', 'jd'] },
    { key: 'benefits', labels: ['benefits', 'perks', 'perks & benefits', 'other benefits'] },
    { key: 'response', labels: ['response', 'remarks', 'notes', 'client notes'] },
  ];

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('---')) return;

    const colonIdx = trimmed.indexOf(':');
    if (colonIdx === -1) return;

    const rawLabel = trimmed.substring(0, colonIdx).trim().toLowerCase();
    const rawVal = trimmed.substring(colonIdx + 1).trim();

    if (!rawVal) return; // Skip empty value lines

    // Find matching field
    const matched = labelMap.find((item) => item.labels.includes(rawLabel));
    if (matched) {
      rawFields[matched.key] = rawVal;
    }
  });

  const parsedData = {};

  // 1. Process Text Fields
  ['jobTitle', 'jobLocation', 'Area', 'education', 'gender', 'weekOff', 'requiredSkills', 'keyResponsibility', 'benefits', 'response'].forEach((field) => {
    if (rawFields[field] !== undefined) {
      parsedData[field] = sanitizeText(rawFields[field]);
    }
  });

  // 2. Process Numeric: Openings
  if (rawFields.numberOfRequirements !== undefined) {
    const num = parseNumericValue(rawFields.numberOfRequirements);
    if (num !== null && num >= 0) {
      parsedData.numberOfRequirements = String(Math.floor(num));
    } else {
      warnings.push(`Invalid number format for Openings: "${rawFields.numberOfRequirements}"`);
    }
  }

  // 3. Process Numeric: Salary
  if (rawFields.salaryMonthly !== undefined) {
    const num = parseNumericValue(rawFields.salaryMonthly);
    if (num !== null && num >= 0) {
      parsedData.salaryMonthly = String(num);
      parsedData.salary = String(num);
    } else {
      warnings.push(`Invalid salary format: "${rawFields.salaryMonthly}"`);
    }
  }

  // 4. Process Numeric: Experience
  if (rawFields.experience !== undefined) {
    const num = parseNumericValue(rawFields.experience);
    if (num !== null && num >= 0) {
      parsedData.experience = String(num);
    } else {
      warnings.push(`Invalid experience format: "${rawFields.experience}"`);
    }
  }

  // 5. Process Job Timing
  if (rawFields.jobTiming !== undefined) {
    parsedData.jobTiming = rawFields.jobTiming;
    const { start, end } = parseJobTimingString(rawFields.jobTiming);
    parsedData.jobTimingStart = start;
    parsedData.jobTimingEnd = end;
  }

  // 6. Process Company & Branch Mapping
  let matchedCompany = null;
  let matchedBranch = null;

  if (rawFields.companyName) {
    const searchCoName = rawFields.companyName.toLowerCase();
    matchedCompany = companyOptions.find(
      (c) => (c.companyName || '').toLowerCase() === searchCoName
    );

    if (!matchedCompany) {
      // Partial match fallback
      matchedCompany = companyOptions.find((c) =>
        (c.companyName || '').toLowerCase().includes(searchCoName) ||
        searchCoName.includes((c.companyName || '').toLowerCase())
      );
    }

    if (matchedCompany) {
      parsedData.companyName = matchedCompany.companyName;
      parsedData.companyId = matchedCompany.companyId;

      // Now match branch if provided
      if (rawFields.branchName && matchedCompany.branches && matchedCompany.branches.length > 0) {
        const searchBrName = rawFields.branchName.toLowerCase();
        matchedBranch = matchedCompany.branches.find(
          (b) => (b.branchName || '').toLowerCase() === searchBrName
        );
        if (!matchedBranch) {
          matchedBranch = matchedCompany.branches.find((b) =>
            (b.branchName || '').toLowerCase().includes(searchBrName) ||
            searchBrName.includes((b.branchName || '').toLowerCase())
          );
        }

        if (matchedBranch) {
          parsedData.branchName = matchedBranch.branchName;
          parsedData.branchId = matchedBranch._id;
          parsedData.selectedBranch = matchedBranch;
        } else {
          warnings.push(`Branch "${rawFields.branchName}" not found under company "${matchedCompany.companyName}".`);
          parsedData.branchName = rawFields.branchName; // Keep text representation
        }
      } else if (rawFields.branchName) {
        parsedData.branchName = rawFields.branchName;
      }
    } else {
      warnings.push(`Company "${rawFields.companyName}" not found in registered company options.`);
      parsedData.companyName = rawFields.companyName;
      if (rawFields.branchName) parsedData.branchName = rawFields.branchName;
    }
  }

  // 7. Calculate Diff with currentFormState
  const fieldDescriptors = [
    { key: 'companyName', label: 'Company' },
    { key: 'branchName', label: 'Branch' },
    { key: 'jobTitle', label: 'Job Title' },
    { key: 'jobLocation', label: 'Job Location' },
    { key: 'Area', label: 'Area' },
    { key: 'numberOfRequirements', label: 'No. of Openings' },
    { key: 'jobTiming', label: 'Job Timing' },
    { key: 'education', label: 'Education' },
    { key: 'gender', label: 'Gender' },
    { key: 'salaryMonthly', label: 'Monthly Salary (₹)' },
    { key: 'experience', label: 'Experience (Years)' },
    { key: 'weekOff', label: 'Week Off' },
    { key: 'requiredSkills', label: 'Required Skills' },
    { key: 'keyResponsibility', label: 'Key Responsibilities' },
    { key: 'benefits', label: 'Benefits' },
    { key: 'response', label: 'Response' },
  ];

  const diff = fieldDescriptors.map(({ key, label }) => {
    let currentVal = currentFormState[key] != null ? String(currentFormState[key]) : '';
    if (key === 'jobTiming' && (currentFormState.jobTimingStart || currentFormState.jobTimingEnd)) {
      currentVal = `${currentFormState.jobTimingStart || ''} - ${currentFormState.jobTimingEnd || ''}`.trim();
    }

    const importedVal = parsedData[key] != null ? String(parsedData[key]) : '';
    const isPresent = parsedData[key] !== undefined;

    let status = 'missing'; // absent in imported text -> kept
    if (isPresent) {
      if (importedVal.trim().toLowerCase() === currentVal.trim().toLowerCase()) {
        status = 'unchanged';
      } else {
        status = 'changed';
      }
    }

    return {
      key,
      label,
      currentVal,
      importedVal: isPresent ? importedVal : '(Not Provided - Kept)',
      status,
      isPresent,
    };
  });

  return {
    parsedData,
    diff,
    warnings,
    hasFields: Object.keys(parsedData).length > 0,
  };
};
